import React, { useState } from 'react';

import styled from 'styled-components';

import { ServiceMore } from '@components/common/Badge';

import { regionName, serviceName } from '@utils/find-name';

import { Partner } from '@customTypes/Partner';
import { Regions } from '@customTypes/Region';
import { Services } from '@customTypes/Service';

interface Props {
  partner: Partner;
  services: Services;
  regions: Regions;
}

interface CollapserProps {
  isCollapsed: boolean;
  hasCollapse: boolean;
  setCollapse: (next: boolean) => void;
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  color: #62656a;
  p {
    white-space: pre-wrap;
    line-height: 160%;
  }
`;

const Title = styled.h6`
  margin-bottom: 30px;
  font-size: 16px;
  font-weight: 700;
  color: #080808;

  &:not(:first-child) {
    margin-top: 50px;
  }
  @media (min-width: 992px) {
    &:not(:first-child) {
      margin-top: 70px;
    }
  }
`;

const Description = styled.p<{ isCollapsed: boolean }>`
  display: -webkit-box;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-line-clamp: ${({ isCollapsed }) => (isCollapsed ? '10' : 'none')};
  -webkit-box-orient: vertical;
`;

const BadgeList = styled.div<{ isCollapsed: boolean }>`
  display: ${({ isCollapsed }) => (isCollapsed ? '-webkit-box' : 'flex')};
  overflow: ${({ isCollapsed }) => (isCollapsed ? 'hidden' : 'none')};
  text-overflow: ${({ isCollapsed }) => (isCollapsed ? 'ellipsis' : 'none')};
  -webkit-line-clamp: ${({ isCollapsed }) => (isCollapsed ? '3' : 'none')};
  -webkit-box-orient: ${({ isCollapsed }) => (isCollapsed ? 'vertical' : 'horizontal')};
  @media (min-width: 992px) {
    -webkit-line-clamp: ${({ isCollapsed }) => (isCollapsed ? '2' : 'none')};
  }
  width: 100%;
  max-width: 622px;
  white-space: wrap;
  flex-wrap: wrap;
`;

const Badge = styled.div`
  display: inline-block;
  margin: 4px 8px 4px 0px;
  padding: 7px 16px;
  color: #62656a;
  font-size: 14px;
  font-weight: 500;
  background-color: #f3f4f8;
  border-radius: 16px;
  @media (min-width: 992px) {
    margin-bottom: 14px;
  }
`;

const PartnerInfo: React.FC<Props> = (props) => {
  const { partner, services, regions } = props;
  const { name, service_ids, coverages, description: $description } = partner;
  const description = $description || `안녕하세요. ${name} 파트너입니다.`;
  const [$collapsedInfo, setCollapsedInfo] = useState(true);
  const [$collapsedServices, setCollapsedServices] = useState(true);
  const [$collapsedRegions, setCollapsedRegions] = useState(true);

  const hasCollapseInfo = description.length > 500;
  const hasCollapseServices = service_ids.length >= 8;
  const hasCollapseRegions = coverages.length >= 10;
  const isCollapsedInfo = hasCollapseInfo && $collapsedInfo;
  const isCollapsedServices = hasCollapseServices && $collapsedServices;
  const isCollapsedRegions = hasCollapseRegions && $collapsedRegions;

  const Collapser = (props: CollapserProps) => {
    const { isCollapsed, hasCollapse, setCollapse } = props;
    return (
      <>
        {isCollapsed ? <ServiceMore onClick={() => setCollapse(false)}>더보기</ServiceMore> : null}
        {!isCollapsed && hasCollapse ? (
          <ServiceMore onClick={() => setCollapse(true)}>간략하게 보기</ServiceMore>
        ) : null}
      </>
    );
  };

  function renderServiceBadges() {
    const serviceNames = service_ids.map((serviceId) => serviceName(serviceId, services));
    const filteredDuplicatedServiceNames = [...new Set(serviceNames)];

    return filteredDuplicatedServiceNames.map((serviceName, i) => <Badge key={i}>{serviceName}</Badge>);
  }

  return (
    <Wrapper>
      <Title>제공 서비스</Title>
      <BadgeList isCollapsed={isCollapsedServices}>{renderServiceBadges()}</BadgeList>
      <Collapser
        isCollapsed={isCollapsedServices}
        hasCollapse={hasCollapseServices}
        setCollapse={setCollapsedServices}
      />
      <Title>소개</Title>
      <Description isCollapsed={isCollapsedInfo}>{description}</Description>
      <Collapser isCollapsed={isCollapsedInfo} hasCollapse={hasCollapseInfo} setCollapse={setCollapsedInfo} />
      <Title>서비스 지역</Title>

      <BadgeList isCollapsed={isCollapsedRegions}>
        {coverages.map((regionId, i) => (
          <Badge key={i}>{regionName(regionId, regions)}</Badge>
        ))}
      </BadgeList>
      <Collapser isCollapsed={isCollapsedRegions} hasCollapse={hasCollapseRegions} setCollapse={setCollapsedRegions} />
    </Wrapper>
  );
};

export default PartnerInfo;
